/* eslint-disable @typescript-eslint/prefer-optional-chain */
import { StatusCodes } from 'http-status-codes';
import React, { useEffect, useState } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import {
  QuantityGetDto,
  toLocalEquivalentUtcDate,
} from 'svila-common';
import { v4 as uuid } from 'uuid';
import styled from 'styled-components';
import _ from 'lodash';
import { saveAs } from 'file-saver';
import PrimaryButton from '../../../../components/common/forms/Button/PrimaryButton';
import TitleComponent from '../../../../components/common/forms/Title/TitleComponent';
import SecondaryNavigation from '../../../../components/common/navigation/SecondaryNavigation';
import WhitePad from '../../../../components/common/WhitePad/WhitePad';
import InventoryApi from '../../../../services/api/InventoryApi';
import tickIcon from '../../../../static/img/SVG/accept_icon.svg';
import PuffLoader from 'react-spinners/PuffLoader';
import OrdersApi from '~/src/services/api/OrdersApi';
import TableWrapper from '~/src/components/common/forms/TableWrapper/TableWrapper';
import GtinComponent from '~/src/components/common/GtinComponent';
import CheckboxInputComponent from '~/src/components/common/forms/CheckboxInput/CheckboxInputComponent';
import moment from 'moment';
import { DATE_FORMAT, ROLES } from '~/src/utils/constants';
import RolesDependent from '~/src/components/common/RolesDependent';
import { quantityGetDtoToStockTransactionAddDto } from '~/src/types/convertors/quantityGetDtoToStockTransactionAddDto';
import { StockTransactionAddDto } from '~/src/types/StockTransactionAddDto';

interface ParamProps {
  id: string;
  consolidation: boolean;
}

interface DataRowTypes {
  index: number;
  barcode: string;
  name: string;
  fullDuUnits: number;
  singleDpUnits: number;
  singleCuUnits: number;
  totalCuUnits: number;
  handleClick: any;
  isChecked: any;
  id: string;
  isInvoiced: boolean;
  isRemoved: boolean;
  soldRelatedTransactionName: string;
  isSold: boolean;
  genericId: string;
  lot: string;
  expirationDate: string;
  daysInStock: number;
  warehouseName: string;
}

export default function Invoice(): React.ReactElement {
  const { id, consolidation } = useParams<ParamProps>();
  const history = useHistory();
  const [copyResult, setCopyResult] = useState('');
  const [union, setUnion] = useState<QuantityGetDto[]>([]);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [isCheckAll, setIsCheckAll] = useState(false); // CheckAll Button Status
  const [isCheck, setIsCheck] = useState([]); // List of single checked items
  const [isExporting, setIsExporting] = useState(false);
  const [markInvoicedLoading, setMarkInvoicedLoading] = useState(false);

  const copyNonInvoiced = async (): Promise<void> => {
    const nonInvoiced: QuantityGetDto[] = [];
    union.forEach((element) => {
      if (element.isInvoiced == false) {
        nonInvoiced.push(element);
      }
    });

    let ttc = `MNum\tbatch\tMName\tfullDuUnits\tsingleDpUnits\tsingleCuUnits\ttotalCuUnits\tdaysInStock\tisRemoved\tisSold\tsoldTo\n`;
    nonInvoiced.forEach((element) => {
      const date = moment(toLocalEquivalentUtcDate(element.expirationDate)).format(DATE_FORMAT);
      ttc += `${element.cuGtin}\t${date}/${element.lot}\t${element.product.genericProduct.name}\t${element.fullDuUnits}\t${element.singleDpUnits}\t${element.singleCuUnits}\t${element.totalCuUnits}\t${element.daysInStock}\t${element.isRemoved}\t${element.isSold}\t${element.soldRelatedTransactionName}\n`;
    });

    const type = 'text/plain';
    const blob = new Blob([ttc], { type });
    const clipboardItem = new ClipboardItem({ [type]: blob });
    const data = [clipboardItem];

    navigator.clipboard.write(data).then(
      function () {
        setCopyResult('Copied');
      },
      function () {
        setCopyResult("Couldn't copy");
      },
    );
  };

  const copyAll = async (): Promise<void> => {
    let ttc = `MNum\tbatch\tMName\tfullDuUnits\tsingleDpUnits\tsingleCuUnits\ttotalCuUnits\tdaysInStock\tisRemoved\tisSold\tsoldTo\n`;
    union.forEach((element) => {
      const date = moment(toLocalEquivalentUtcDate(element.expirationDate)).format(DATE_FORMAT);
      ttc += `${element.cuGtin}\t${date}/${element.lot}\t${element.product.genericProduct.name}\t${element.fullDuUnits}\t${element.singleDpUnits}\t${element.singleCuUnits}\t${element.totalCuUnits}\t${element.daysInStock}\t${element.isRemoved}\t${element.isSold}\t${element.soldRelatedTransactionName}\n`;
    });

    const type = 'text/plain';
    const blob = new Blob([ttc], { type });
    const clipboardItem = new ClipboardItem({ [type]: blob });
    const data = [clipboardItem];

    navigator.clipboard.write(data).then(
      function () {
        setCopyResult('Copied');
      },
      function () {
        setCopyResult("Couldn't copy");
      },
    );
  };

  const markInvoiced = async (): Promise<void> => {
    setMarkInvoicedLoading(true);
    const invoiced: StockTransactionAddDto[] = [];
    isCheck.forEach((element) => {
      const item = union.find((e) => element == e.tempId);
      if (item) {
        const converted = quantityGetDtoToStockTransactionAddDto(item);
        invoiced.push(converted);
      }
    });

    const result = await InventoryApi.markAsInvoiced(invoiced);
    if (result?.status === StatusCodes.OK) {
      getQuantities();
    }
    setMarkInvoicedLoading(false);
  };

  const markNonInvoiced = async () => {
    setMarkInvoicedLoading(true);
    const invoiced: StockTransactionAddDto[] = [];
    isCheck.forEach((element) => {
      const item = union.find((e) => element == e.tempId);
      if (item) {
        const converted = quantityGetDtoToStockTransactionAddDto(item);
        invoiced.push(converted);
      }
    });

    const result = await InventoryApi.markAsNonInvoiced(invoiced);
    if (result.status === StatusCodes.OK) {
      getQuantities();
    }
    setMarkInvoicedLoading(false);
  };

  const getQuantities = async (): Promise<void> => {
    setLoadingData(true);
    const result = await InventoryApi.getQuantitiesForSellOrder(id);
    if (result.status === StatusCodes.OK) {
      setUnion(result.data);
    }
    setLoadingData(false);
  };

  useEffect(() => {
    if (id) {
      getQuantities();
    }
  }, []);

  const handleSelectAll = (e) => {
    setIsCheckAll(!isCheckAll);
    setIsCheck(union.map((li) => li.tempId));
    if (isCheckAll) {
      setIsCheck([]);
    }
  };

  const handleClick = (e) => {
    const { id, checked } = e.target;
    setIsCheck([...isCheck, id]);
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id));
    }
  };

  const DataRow = ({
    barcode,
    name,
    fullDuUnits,
    singleDpUnits,
    singleCuUnits,
    totalCuUnits,
    index,
    handleClick,
    isChecked,
    id,
    isInvoiced,
    isRemoved,
    soldRelatedTransactionName,
    isSold,
    genericId,
    lot,
    expirationDate,
    daysInStock,
    warehouseName
  }: DataRowTypes): React.ReactElement => {
    const date = moment(toLocalEquivalentUtcDate(expirationDate)).format(DATE_FORMAT);

    return (
      <tr>
        <td style={{ padding: 10, border: '1px solid gray' }}>{index + 1}</td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          <CheckboxInputComponent onChange={handleClick} checked={isChecked} id={id} name="myCheck" />
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          {isInvoiced && <img src={tickIcon} style={{ width: 20, height: 20 }} />}
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          <GtinComponent text="CU" code={barcode} background="#F4F4F4" />
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>{`${date} ${lot} (${warehouseName || 'Unknown Warehouse'}) `}</td>
        <td style={{ padding: 10, border: '1px solid gray', textAlign: 'left', color: '#343434' }}>
          <Link style={{ color: '#343434' }} to={`/inventory/list-for-generic/${genericId}`} target="_blank">
            {name}
          </Link>
        </td>
        <td style={{ padding: 10, border: '1px solid gray', fontWeight: 'bold' }}>{fullDuUnits}</td>
        <td style={{ padding: 10, border: '1px solid gray' }}>{singleDpUnits}</td>
        <td style={{ padding: 10, border: '1px solid gray' }}>{singleCuUnits}</td>
        <td style={{ padding: 10, border: '1px solid gray' }}>{totalCuUnits}</td>
        <td style={{ padding: 10, border: '1px solid gray' }}>{daysInStock ?? '—'}</td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          {isRemoved && <img src={tickIcon} style={{ width: 20, height: 20 }} />}
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          {isSold && <img src={tickIcon} style={{ width: 20, height: 20 }} />}
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>{soldRelatedTransactionName}</td>
      </tr>
    );
  };

  const rowSum = (item: QuantityGetDto[]): React.ReactElement => {
    let fullDuUnitsSum = 0;
    let singleDpUnitsSum = 0;
    let singleCuUnitsSum = 0;
    let totalCuUnitsSum = 0;
    for (let i = 0; i < item.length; i++) {
      fullDuUnitsSum = fullDuUnitsSum + (item[i].fullDuUnits ?? 0);
      singleDpUnitsSum = singleDpUnitsSum + (item[i].singleDpUnits ?? 0);
      singleCuUnitsSum = singleCuUnitsSum + (item[i].singleCuUnits ?? 0);
      totalCuUnitsSum = totalCuUnitsSum + item[i].totalCuUnits;
    }

    return (
      <tr>
        <td style={{ padding: 10, border: '1px solid gray' }} colSpan={6}></td>
        <td style={{ padding: 10, border: '1px solid gray', fontWeight: 'bold' }}>{fullDuUnitsSum}</td>
        <td style={{ padding: 10, border: '1px solid gray' }}>
          <span>{singleDpUnitsSum}</span>
        </td>
        <td style={{ padding: 10, border: '1px solid gray' }}>{singleCuUnitsSum}</td>
        <td style={{ padding: 10, border: '1px solid gray' }}>{totalCuUnitsSum}</td>
        <td style={{ padding: 10, border: '1px solid gray' }}></td>
      </tr>
    );
  };

  const getCustomsExcel = async (isPackingList = false): Promise<void> => {
    setIsExporting(true);
    let x = null;
    if (isPackingList) {
      x = await OrdersApi.getCustomsPackingListExcel(id);
    } else {
      x = await OrdersApi.getCustomsExcel(id);
    }

    setIsExporting(false);
    const courseSheet = x.data;
    if (courseSheet) {
      const blob = new Blob([courseSheet], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(blob, `Full-Customs-Excel.xlsx`);
    }
  };

  return (
    <WhitePad>
      <SecondaryNavigation isConsolidation={consolidation} />
      <TitleComponent
        title="Colibri Invoice Data"
        onLeftHandler={(): void => {
          history.push('/orders-list');
        }}
      />
      <ActionWrapper>
        <RolesDependent roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE, ROLES.WAREHOUSE_ADMIN, ROLES.SVILA_CLIENT_ADMIN]}>
          <PrimaryButton isBig title="Get Customs Excel" onClick={() => getCustomsExcel(false)} />
          <PrimaryButton title="Get Customs Packing List Excel" onClick={() => getCustomsExcel(true)} />
        </RolesDependent>
      </ActionWrapper>
      {union &&
        (loadingData ? (
          <PuffLoader color="#000000" loading={true} size={25} />
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <ActionWrapper>
                <RolesDependent roles={[ROLES.ADMIN, ROLES.SUPER_ADMIN, ROLES.WAREHOUSE, ROLES.WAREHOUSE_ADMIN, ROLES.SVILA_CLIENT_ADMIN]}>
                  <PrimaryButton
                    isBig
                    title="Mark invoiced"
                    onClick={markInvoiced}
                    isSubmitting={markInvoicedLoading}
                  />
                  <PrimaryButton
                    title="Mark non-invoiced"
                    onClick={markNonInvoiced}
                    isSubmitting={markInvoicedLoading}
                  />
                </RolesDependent>
              </ActionWrapper>
              <ActionWrapper>
                <PrimaryButton isBig title="Copy All" onClick={copyAll} />
                <PrimaryButton title="Copy non-invoiced" onClick={copyNonInvoiced} />
              </ActionWrapper>
            </div>
            &nbsp;
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <p></p>
              <p>{copyResult}</p>
            </div>
            <br />
            <br />
            <br />
            <TableWrapper>
              <thead>
                <tr>
                  <TableHeader>№</TableHeader>
                  <th
                    style={{
                      padding: '10px',
                      border: '1px solid gray',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-evenly',
                    }}
                  >
                    All
                    <CheckboxInputComponent
                      onChange={handleSelectAll}
                      checked={isCheckAll}
                      id="myCheck"
                      name="myCheck"
                    />
                  </th>
                  <TableHeader>Invoiced</TableHeader>
                  <TableHeader>MNum</TableHeader>
                  <TableHeader>batch</TableHeader>
                  <TableHeader>MName</TableHeader>
                  <TableHeader>Du</TableHeader>
                  <TableHeader>Dp</TableHeader>
                  <TableHeader>Cu</TableHeader>
                  <TableHeader>Total Cu</TableHeader>
                  <TableHeader>Days In Stock</TableHeader>
                  <TableHeader>Removed</TableHeader>
                  <TableHeader>Sold</TableHeader>
                  <TableHeader>Sold To</TableHeader>
                </tr>
              </thead>
              <tbody>
                {union &&
                  union.map((item, index) => {
                    const idx: string = uuid() as string;

                    return (
                      <DataRow
                        handleClick={handleClick}
                        isChecked={isCheck.includes(item.tempId)}
                        id={item.tempId}
                        index={index}
                        key={idx}
                        barcode={item.cuGtin}
                        name={item.product.genericProduct.name}
                        fullDuUnits={item.fullDuUnits ?? 0}
                        singleDpUnits={item.singleDpUnits ?? 0}
                        singleCuUnits={item.singleCuUnits ?? 0}
                        totalCuUnits={item.totalCuUnits}
                        isInvoiced={item.isInvoiced}
                        isRemoved={item.isRemoved}
                        soldRelatedTransactionName={item.soldRelatedTransactionName}
                        isSold={item.isSold}
                        genericId={item.product.genericProductId}
                        lot={item.lot}
                        expirationDate={item.expirationDate}
                        daysInStock={item.daysInStock}
                        warehouseName={item.warehouse?.name ?? ''}
                      />
                    );
                  })}
                {union && rowSum(union)}
              </tbody>
            </TableWrapper>
          </>
        ))}
    </WhitePad>
  );
}

const ActionWrapper = styled.div`
  display: flex;
  margin-bottom: 10px;

  div {
    margin-right: 5px;
  }
`;

const TableHeader = styled.th`
  padding: 10px;
  border: 1px solid gray;
  text-align: center;
`;